import { isEuropeanCountry } from '../../../../lib/middleware/variantRedirect.helper'
import {
  BanknotesIcon,
  BuildingStorefrontIcon,
  ChatBubbleLeftRightIcon,
  ReceiptPercentIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import { useCountryShippingStatusQuery } from '@/generated/graphql'
import { getShippingFeatureCopy } from './shippingFeatureCopy'
import {
  getCountryDisplayName,
  normalizeCountryIsoCode,
} from '@/countryDisplay'

const featureCopy = (selectedCountry: string, freeShipping?: boolean) => {
  const eu = isEuropeanCountry(selectedCountry)
  const uk = normalizeCountryIsoCode(selectedCountry) === 'GB'
  const shippingFeature = getShippingFeatureCopy(freeShipping)
  const countryName = getCountryDisplayName(selectedCountry) || 'your country'
  return [
    {
      icon: BuildingStorefrontIcon,
      title: eu ? 'Ships from our UK warehouse' : 'Ships from California',
      description: eu
        ? `Orders to ${countryName} dispatch from the UK store.`
        : 'US and international orders dispatch from California.',
    },
    {
      icon: BanknotesIcon,
      title: eu ? 'GBP checkout (£)' : 'USD checkout ($)',
      description: eu
        ? 'Product pages, basket, and payment stay in pounds.'
        : 'Product pages, basket, and payment stay in dollars.',
    },
    ...(uk
      ? []
      : [
          {
            icon: ReceiptPercentIcon,
            title: eu
              ? 'VAT and duties shown early'
              : 'Taxes shown before payment',
            description: eu
              ? 'When customs costs can be calculated, we show them before you pay.'
              : 'Checkout totals update before you place your order.',
          },
        ]),
    ...(shippingFeature
      ? [
          {
            icon: TruckIcon,
            title: shippingFeature.title,
            description: shippingFeature.description,
          },
        ]
      : []),
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Human support',
      description: 'Real people are here if anything looks wrong.',
    },
  ]
}

export const Features = ({ selectedCountry }: FeaturesProps) => {
  const { data } = useCountryShippingStatusQuery({
    isoCode: selectedCountry,
  })
  const items = featureCopy(
    selectedCountry,
    data?.countryByIsoCode?.freeShipping
  )

  return (
    <div>
      <ul className="grid gap-4 sm:grid-cols-2">
        {items.map(({ icon: Icon, title, description }) => (
          <li
            key={title}
            className="flex gap-4 rounded-xl border border-gray-100 bg-gray-50/60 p-4 sm:p-5"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white text-green-700 shadow-sm ring-1 ring-gray-200/80">
              <Icon className="h-6 w-6" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block text-base font-semibold text-gray-900 md:text-lg">
                {title}
              </span>
              <span className="mt-1 block text-base leading-snug text-gray-600">
                {description}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface FeaturesProps {
  selectedCountry: string
}
